/**
 * Gravar um áudio pelo microfone para anexar na conversa.
 *
 * Só a escolha do codec e o nome do arquivo moram aqui — são puros e
 * testáveis sem navegador. O resto (getUserMedia + MediaRecorder) é
 * orquestração de API do navegador e mora no componente, igual ao
 * `enviandoAnexo` do anexo por arquivo em `Window.tsx`.
 */

// Ordem de preferência: quem decide o que REALMENTE grava é o navegador — isto
// só escolhe ENTRE os formatos que ele já sabe fazer. Chrome/Firefox/Android
// gravam opus em webm; Safari/iOS não tem webm e grava mp4/aac.
const CANDIDATOS_MIME = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
] as const

/**
 * O primeiro formato da lista que o gravador aceita.
 *
 * Recebe o checador (em vez de chamar `MediaRecorder.isTypeSupported` direto)
 * para o teste não depender de um navegador de verdade.
 */
export function escolherMimeType(
  ehSuportado: (tipo: string) => boolean,
  candidatos: readonly string[] = CANDIDATOS_MIME,
): string {
  for (const candidato of candidatos) {
    if (ehSuportado(candidato)) return candidato
  }
  return ""
}

const EXTENSAO_POR_MIME: Record<string, string> = {
  "audio/webm": "webm",
  "audio/mp4": "mp4",
  "audio/ogg": "ogg",
}

/** Nome do arquivo gravado, com a extensão do mime efetivo (o que o gravador realmente usou). */
export function nomeDoArquivoGravado(mime: string, agora: number = Date.now()): string {
  const base = String(mime || "").split(";")[0].trim().toLowerCase()
  const extensao = EXTENSAO_POR_MIME[base] || "webm"
  return `audio-${agora}.${extensao}`
}

/** Se este navegador sabe gravar áudio pelo microfone. */
export function suportaGravacao(): boolean {
  try {
    return (
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder === "function"
    )
  } catch {
    return false
  }
}

/** "1:05" — minutos sem zero à esquerda, segundos com. */
export function formatarDuracao(segundos: number): string {
  const minutos = Math.floor(segundos / 60)
  const resto = segundos % 60
  return `${minutos}:${String(resto).padStart(2, "0")}`
}
